import React, { useCallback, useMemo } from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { AssetContainer } from '@techstyle/react-assets';
import { FormattedMessage } from '@techstyle/react-intl';

import { mobile } from '../../styles';
import { BasicButton } from '../../styles/button';
import { toStringSet } from '../../utils/assets';
import { useGshop } from '../../utils/gshop';
import { useLDFlags } from '../../utils/LD/useLDFlags';
import { useMembership } from '../../utils/useMembership';
import { Component as Price } from '../ExtendedPrice';
import { Component as ProductDetailCTA } from '../ProductDetailCTA';
import { useProductDetailContext } from '../ProductDetailLayout/ProductDetailContext';
import { ProductDetailPriceModeLegalText } from '../ProductDetailPriceModeLegalText/ProductDetailPriceModeLegalText';

const Container = styled.div`
  margin-top: ${props => props.theme.spacing.moderate}px;

  ${mobile`
    width: calc(100% + ${props => props.theme.spacing.medium}px);
    margin-left: -${props => props.theme.spacing.small}px;
  `}

  // This targets the succeeding siblings after the first child.
  // Useful when the order/arrangement is dynamic.
  & > * + * {
    ${({ theme: { spacing } }) => `margin-top: ${spacing.small}px;`}

    ${mobile`
      ${({ theme: { spacing } }) => `margin-top: ${spacing.moderate}px;`}
    `}
  }
`;

const SingleCTAContainer = styled.div`
  ${mobile`
    padding:0 ${props => props.theme.spacing.small}px;
  `}
`;

const VipContainer = styled.div`
  ${mobile`
    padding: ${props => props.theme.spacing.small}px;
  `}
`;

const GuestContainer = styled.div`
  ${mobile`
    padding: ${props => props.theme.spacing.small}px;
  `}
`;

const GuestText = styled.div`
  ${({ theme }) => theme.paragraph.variants.paragraph3Uppercase.textStyles}
  color: ${({ theme }) => theme.colors.gunmetal};
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: space-between;
`;

const VipText = styled.div`
  display: flex;
  flex-wrap: wrap;
  text-align: center;

  ${({ theme }) => theme.paragraph.variants.paragraph3Uppercase.textStyles}
  color: ${({ theme }) => theme.colors.gunmetal};
  gap: 4px;
  justify-content: space-between;
`;

const ChoosePriceButton = styled(BasicButton)`
  width: 100%;
  margin-bottom: 15px;
`;

const VipButton = styled(ProductDetailCTA)`
  & ${ProductDetailCTA.ProductDetailAddToCartButton} {
    width: 100%;

    text-align: center;
    margin-top: 11px;
  }
`;

const GuestButton = styled(VipButton)`
  & ${ProductDetailCTA.ProductDetailAddToCartButton} {
    ${({ theme: { colors, spacing, button } }) => css`
      width: 100%;
    `}
  }
`;

const SingleCTAVipButton = styled(ProductDetailCTA)`
  & ${ProductDetailCTA.ProductDetailAddToCartButton} {
    width: 100%;
    text-align: center;
    margin-top: 11px;
  }
`;

const SingleCTAGuestButton = styled(VipButton)`
  & ${ProductDetailCTA.ProductDetailAddToCartButton} {
    ${({ theme: { colors, spacing, button } }) => css`
      width: 100%;
    `}
  }
`;

const StyledPrice = styled(Price)`
  color: ${({ theme }) => theme.colors.gunmetal};
`;

const PromoText = styled.div`
  text-align: center;
  margin-left: 4px;

  ${mobile`
    display: block;
  `}

  ${({ theme }) => theme.paragraph.variants.paragraph3Uppercase.textStyles}
    color: ${({ theme }) => theme.colors.gunmetal};
`;

const BundleWrapper = styled.div`
  display: flex;

  color: ${({ theme }) => theme.colors.gunmetal};
`;

const ProductDetailVipToggleButtons = ({
  allowExclusiveCheckout,
  availableQuantity,
  cartButtonDisabled,
  className,
  guestAmount,
  isBorderfreeCustomer,
  isExclusive,
  isPreorderable,
  isProductSet,
  isSoldOut,
  isVip,
  isXtraVipBox,
  location,
  onClick = () => {},
  vipAmount,
  shouldNotShowCtaForSports,
  shouldShowPromoPrice,
  showToastMessage,
  shouldShowNewProductPricing,
  memberPrice,
}) => {
  const { isDowngraded, price: membershipPrice } = useMembership();
  const { 'emp-membership-onboarding': isEmpFeatureEnabled } = useLDFlags();
  const { isGshopActive } = useGshop();
  const {
    singleCTA: {
      showPriceSelectors,
      priceOptionSelected,
      incrementPriceSelectorError,
    },
  } = useProductDetailContext();
  const hideButtons = !isBorderfreeCustomer && isXtraVipBox && isSoldOut;

  const labelText = useMemo(() => {
    if (isExclusive) {
      return (
        <FormattedMessage
          id="site_pdp.shop_exclusive_price_toggle"
          defaultMessage="VIP Exclusive Only:"
        />
      );
    } else if (isDowngraded) {
      return shouldShowNewProductPricing ? (
        <FormattedMessage
          id="site_pdp.new_shop_vip_price_toggle"
          defaultMessage="Members:"
        />
      ) : (
        <FormattedMessage
          id="site_pdp.shop_vip_price_toggle_downgraded"
          defaultMessage="Member Offer:"
        />
      );
    }

    return shouldShowNewProductPricing ? (
      <FormattedMessage
        id="site_pdp.new_shop_vip_price_toggle"
        defaultMessage="Members:"
      />
    ) : (
      <FormattedMessage
        id="site_pdp.shop_vip_price_toggle"
        defaultMessage="New Rewards Member Offer:"
      />
    );
  }, [isDowngraded, isExclusive, shouldShowNewProductPricing]);

  const addToCart = useCallback(
    option => () => {
      if (shouldNotShowCtaForSports) {
        showToastMessage(
          <FormattedMessage
            defaultMessage="Sport items are for VIPS only"
            id="site_pdp.sport_items_are_for_vips_only"
          />
        );
      } else {
        onClick(option);
      }
    },
    [onClick, shouldNotShowCtaForSports, showToastMessage]
  );

  const showPromoPlusOverlayText = useCallback(
    ({ overlayText, pdpLabel }) => {
      if (overlayText) {
        return (
          <BundleWrapper>
            <StyledPrice
              amount={vipAmount}
              shouldShowPromoPrice={shouldShowPromoPrice}
            />
            &nbsp;
            <FormattedMessage id="site_pdp.or" defaultMessage="or" />
            <PromoText data-autotag="pdp-non-vip-promo">
              {overlayText}
            </PromoText>
          </BundleWrapper>
        );
      }

      return (
        <PromoText
          data-autotag="pdp-non-vip-promo"
          dangerouslySetInnerHTML={pdpLabel ? { __html: pdpLabel } : null}
        />
      );
    },
    [shouldShowPromoPrice, vipAmount]
  );

  const renderMemberCTACopy = () => {
    return (
      <ProductDetailPriceModeLegalText
        location={location}
        shouldNotShowCtaForSports={shouldNotShowCtaForSports}
      />
    );
  };

  const renderVipContainer = useCallback(
    () => (
      <VipContainer>
        <VipText data-autotag="pdpVIPOfferTextLoc">
          {labelText}
          <AssetContainer name="pdp_html_fpl">
            {asset => {
              if (
                !asset ||
                !asset.data ||
                (asset.data &&
                  !toStringSet(asset.data.options.customVars.flags, true).has(
                    'bundle'
                  ))
              ) {
                return (
                  <StyledPrice
                    amount={vipAmount}
                    shouldShowPromoPrice={shouldShowPromoPrice}
                  />
                );
              } else {
                return showPromoPlusOverlayText(asset.data.options.customVars);
              }
            }}
          </AssetContainer>
        </VipText>
        <VipButton
          allowExclusiveCheckout={allowExclusiveCheckout}
          availableQuantity={availableQuantity}
          cartButtonDisabled={cartButtonDisabled}
          isExclusive={isExclusive}
          isPreorderable={isPreorderable}
          isSoldOut={isSoldOut}
          isWaitlistable={false}
          isXtraVipBox={isXtraVipBox}
          onAddToCart={addToCart('vip')}
          shouldColorByPromo
          constructorButtonLabel="vip_add_to_bag"
          variant="primaryLavender"
        >
          {shouldShowNewProductPricing ? (
            <FormattedMessage
              id="site_pdp.new_shop_vip_toggle"
              defaultMessage="ADD TO BAG"
            />
          ) : (
            <FormattedMessage
              id="site_pdp.shop_vip_toggle"
              defaultMessage="VIP ADD TO BAG"
            />
          )}
        </VipButton>
        {renderMemberCTACopy()}
      </VipContainer>
    ),
    [
      addToCart,
      allowExclusiveCheckout,
      availableQuantity,
      cartButtonDisabled,
      isEmpFeatureEnabled,
      isExclusive,
      isPreorderable,
      isSoldOut,
      isXtraVipBox,
      labelText,
      membershipPrice,
      shouldShowNewProductPricing,
      shouldShowPromoPrice,
      showPromoPlusOverlayText,
      vipAmount,
    ]
  );

  const renderGuestContainer = useCallback(() => {
    const guestPriceToggleMessage = shouldShowNewProductPricing ? (
      <FormattedMessage
        id="site_pdp.new_shop_guest_price_toggle"
        defaultMessage="Guests: {regPrice}"
        values={{
          regPrice: null,
        }}
      />
    ) : (
      <FormattedMessage
        id="site_pdp.shop_guest_price_toggle"
        defaultMessage="Guest add to cart - {regPrice}"
        values={{
          regPrice: null,
        }}
      />
    );

    return isPreorderable ? null : (
      <GuestContainer>
        <GuestText disabled={isExclusive}>
          {isExclusive ? (
            <FormattedMessage
              id="site_pdp.shop_guest_exclusive_toggle"
              defaultMessage="Not available for guest checkout"
            />
          ) : (
            <>
              {guestPriceToggleMessage}
              <StyledPrice
                amount={isGshopActive ? memberPrice : guestAmount}
                shouldShowPromoPrice
              />
            </>
          )}
        </GuestText>
        <GuestButton
          allowExclusiveCheckout={allowExclusiveCheckout}
          availableQuantity={availableQuantity}
          cartButtonDisabled={cartButtonDisabled || isExclusive}
          isExclusive={isExclusive}
          isSoldOut={isSoldOut}
          isWaitlistable={false}
          isXtraVipBox={isXtraVipBox}
          onAddToCart={addToCart('guest')}
          constructorButtonLabel="guest_add_to_bag"
          variant="secondaryDark"
        >
          <FormattedMessage
            id="site_pdp.shop_guest_toggle"
            defaultMessage="GUEST ADD TO BAG"
            whiteSpace="nowrap"
          />
        </GuestButton>
      </GuestContainer>
    );
  }, [
    addToCart,
    allowExclusiveCheckout,
    availableQuantity,
    cartButtonDisabled,
    guestAmount,
    isExclusive,
    isGshopActive,
    isPreorderable,
    isSoldOut,
    isXtraVipBox,
    shouldShowNewProductPricing,
    vipAmount,
  ]);

  const renderSingleCTAMember = useCallback(() => {
    return (
      <SingleCTAVipButton
        allowExclusiveCheckout={allowExclusiveCheckout}
        availableQuantity={availableQuantity}
        cartButtonDisabled={cartButtonDisabled}
        isExclusive={isExclusive}
        isPreorderable={isPreorderable}
        isSoldOut={isSoldOut}
        isWaitlistable={false}
        isXtraVipBox={isXtraVipBox}
        onAddToCart={addToCart('vip')}
        shouldColorByPromo
        constructorButtonLabel="vip_add_to_bag"
        variant="primaryLavender"
      >
        <FormattedMessage
          id="site_pdp.single_cta_member_add_to_bag"
          defaultMessage="MEMBER ADD TO BAG"
        />
      </SingleCTAVipButton>
    );
  }, [
    addToCart,
    allowExclusiveCheckout,
    availableQuantity,
    cartButtonDisabled,
    guestAmount,
    isExclusive,
    isPreorderable,
    isSoldOut,
    isXtraVipBox,
    shouldShowNewProductPricing,
  ]);

  const renderSingleCTAGuest = useCallback(() => {
    return (
      <SingleCTAGuestButton
        allowExclusiveCheckout={allowExclusiveCheckout}
        availableQuantity={availableQuantity}
        cartButtonDisabled={cartButtonDisabled || isExclusive}
        isExclusive={isExclusive}
        isSoldOut={isSoldOut}
        isWaitlistable={false}
        isXtraVipBox={isXtraVipBox}
        onAddToCart={addToCart('guest')}
        constructorButtonLabel="guest_add_to_bag"
        variant="primaryDark"
      >
        <FormattedMessage
          id="site_pdp.single_cta_guest_add_to_bag"
          defaultMessage="GUEST ADD TO BAG"
        />
      </SingleCTAGuestButton>
    );
  }, [
    addToCart,
    allowExclusiveCheckout,
    availableQuantity,
    cartButtonDisabled,
    guestAmount,
    isExclusive,
    isPreorderable,
    isSoldOut,
    isXtraVipBox,
    shouldShowNewProductPricing,
  ]);

  const renderSingleCTANeither = () => {
    return (
      <ChoosePriceButton
        variant="primaryDark"
        onClick={incrementPriceSelectorError}
      >
        <FormattedMessage
          id="site_pdp.single_cta_add_to_bag"
          defaultMessage="Add To Bag"
        />
      </ChoosePriceButton>
    );
  };

  if (hideButtons) {
    return null;
  }

  return (
    <Container className={className}>
      <>
        {showPriceSelectors && (
          <SingleCTAContainer>
            {priceOptionSelected === 'neither' && renderSingleCTANeither()}
            {priceOptionSelected === 'guest' && renderSingleCTAGuest()}
            {priceOptionSelected === 'member' && renderSingleCTAMember()}
          </SingleCTAContainer>
        )}

        {!showPriceSelectors ? (
          <>
            {renderVipContainer()}
            {renderGuestContainer()}
          </>
        ) : (
          renderMemberCTACopy()
        )}
      </>
    </Container>
  );
};

ProductDetailVipToggleButtons.propTypes = {
  allowExclusiveCheckout: PropTypes.bool,
  availableQuantity: PropTypes.number,
  cartButtonDisabled: PropTypes.bool,
  className: PropTypes.string,
  guestAmount: PropTypes.number,
  isBorderfreeCustomer: PropTypes.bool,
  isExclusive: PropTypes.bool,
  isPreorderable: PropTypes.bool,
  isSoldOut: PropTypes.bool,
  isVip: PropTypes.bool,
  isXtraVipBox: PropTypes.bool,
  location: PropTypes.string,
  onClick: PropTypes.func,
  shouldNotShowCtaForSports: PropTypes.bool,
  shouldShowPromoPrice: PropTypes.bool,
  showToastMessage: PropTypes.func,
  vipAmount: PropTypes.number,
  memberPrice: PropTypes.number,
};

ProductDetailVipToggleButtons.Container = Container;
ProductDetailVipToggleButtons.GuestContainer = GuestContainer;
ProductDetailVipToggleButtons.VipContainer = VipContainer;
ProductDetailVipToggleButtons.VipText = VipText;
ProductDetailVipToggleButtons.StyledPrice = StyledPrice;
ProductDetailVipToggleButtons.VipButton = VipButton;
ProductDetailVipToggleButtons.GuestText = GuestText;

export default ProductDetailVipToggleButtons;
